/**
 * Issued Tickets — top-level page (2026-08-08, §6.2).
 *
 * Previously only reachable via a toggle button buried inside Conversations
 * (mainView === 'tickets'), with no route/URL/sidebar entry — despite being
 * more central to daily agent work than Automation, which already had its
 * own top-level page. This is a lift-and-shift: IssuedTicketsPanel already
 * exists and works, it just needed to stop being nested inside
 * ConversationsPage's conditional render.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import IssuedTicketsPanel from '../components/conversations/IssuedTicketsPanel';
import { complaintTicketsAPI } from '../services/api';

const TicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketParam = searchParams.get('ticket');

  const [checking, setChecking] = useState(true);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    complaintTicketsAPI
      .isEnabled()
      .then((res) => {
        if (!cancelled) setEnabled(Boolean(res?.enabled));
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground">
        <AlertTriangle className="h-6 w-6" />
        <p className="text-sm">Digital Companion ticketing isn't enabled for this account.</p>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0">
      <IssuedTicketsPanel
        highlightTicketNumber={ticketParam}
        onOpenConversation={(conversationId) => navigate(`/dashboard/conversations/${conversationId}`)}
      />
    </div>
  );
};

export default TicketsPage;
